import { FC, useCallback, useMemo, useState } from 'react';

import { Kebab, Modal, PageHead, Switch, Tabs } from 'views/design-system';

import { Empty } from '../bits';
import { dateLine } from '../data/format';
import { IMonitoringRule, ITxnStatus } from '../data/types';
import { useMonitoringRules, useTxnStats } from '../data/use-tm';

import '../../design-system/tokens.scss';
import '../../design-system/surface.scss';
import '../txn-surface.scss';
import './rules-deck.scss';

/**
 * TRANSACTION MONITORING — RULES.
 *
 * The catalogue the engine scores against, and the only place it can be tuned.
 * Built to `_design/specs/26-tm-rules-page.md`.
 *
 * THE THREE THINGS THIS PAGE HAS TO BE HONEST ABOUT, because each of them looks
 * like a working control otherwise:
 *
 * 1. **A predicate the engine does not implement.** `predicate` names a function
 *    in the engine's PREDICATES map; a row naming one this build lacks is logged
 *    and SKIPPED at evaluation time, which on screen is indistinguishable from a
 *    rule that never matched. The service stamps `predicateImplemented` on every
 *    row for exactly this, and such a row carries a chip saying so.
 * 2. **What "delete" can mean.** Only a tenant's own override can be deleted, and
 *    doing so RESETS the rule to the shared default rather than removing it. A
 *    shared default cannot be deleted at all — it would either change the rule
 *    for every business or be recreated by the seed at the next boot — so that
 *    menu item is present and disabled with the reason, rather than absent.
 * 3. **Stale firing counts.** The Firing column comes from a different endpoint
 *    than the catalogue. When that one fails, the numbers must read as unknown
 *    rather than freeze at their last value under a live-looking header.
 */

const FIRING_WINDOWS = [
	{ label: '1h', minutes: 60 },
	{ label: '24h', minutes: 1440 },
] as const;
/* Resolved through a helper rather than indexed directly: the index is state, so
   the compiler cannot know it is in range, and an assertion at each of the four
   use sites is four places for a real out-of-range to hide. `as const` above
   gives the tuple a known length, so the modulo is provably in range and no
   assertion is needed at all. */
const windowAt = (i: number) => FIRING_WINDOWS[i % FIRING_WINDOWS.length] ?? FIRING_WINDOWS[1];
const MODES = ['All', 'Active', 'Test'];
const STATUSES = ['All', 'Enabled', 'Disabled'];
const SORTS: Record<string, string> = {
	firing: 'Firing',
	score: 'Score',
	name: 'Name',
	bundle: 'Bundle',
};

/* The model's own enum, plus the "no status change" case. Never free text — for
   the same reason `predicate` is never an input: an invalid value here is a rule
   that silently stops doing what it says. */
const CHANGE_STATUSES: (ITxnStatus | '')[] = [
	'',
	'AWAITING_USER',
	'IN_REVIEW',
	'DECLINED',
	'APPROVED',
];

export const RulesDeck: FC = () => {
	const [winIdx, setWinIdx] = useState(1);
	const [modeIdx, setModeIdx] = useState(0);
	const [statusIdx, setStatusIdx] = useState(0);
	const [sort, setSort] = useState('firing');
	const [bundle, setBundle] = useState('All');
	const [q, setQ] = useState('');
	const [configureId, setConfigureId] = useState<string | null>(null);
	const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
	const [saving, setSaving] = useState<string | null>(null);
	const [scoreDraft, setScoreDraft] = useState<Record<string, string>>({});

	const { rules, loading, failed, tune, remove } = useMonitoringRules();
	/* `topRules` is what makes this page's counts complete. The endpoint defaults
	   to the top 7 for the triage deck's leaderboard; asking for 200 here means a
	   rule that fired twice is not reported as never having fired. */
	const { stats, failed: statsFailed } = useTxnStats(windowAt(winIdx).minutes, 200);

	/* ruleName is the join key on purpose: `rulesFiring` is built from the rule
	   runs stored on transactions, and both sides resolve a tenant override to the
	   shared default's NAME (that is how the override works at all), so the id
	   would not match across them. */
	const firingByName = useMemo(() => {
		const m: Record<string, number> = {};
		(stats?.rulesFiring ?? []).forEach(r => {
			m[r.ruleName] = r.count;
		});
		return m;
	}, [stats]);

	const bundles = useMemo(
		() => Array.from(new Set(rules.map(r => r.bundle))).sort(),
		[rules]
	);

	/* A rule that never fired joins to 0 rather than being absent — the whole
	   catalogue is the row set here, not just what showed up in the window. */
	const firingOf = useCallback(
		(r: IMonitoringRule) => (r.predicateImplemented ? (firingByName[r.ruleName] ?? 0) : 0),
		[firingByName]
	);

	const shown = useMemo(() => {
		const needle = q.trim().toLowerCase();
		const rows = rules.filter(r => {
			if (bundle !== 'All' && r.bundle !== bundle) return false;
			if (MODES[modeIdx] === 'Active' && r.mode !== 'ACTIVE') return false;
			if (MODES[modeIdx] === 'Test' && r.mode !== 'TEST') return false;
			if (STATUSES[statusIdx] === 'Enabled' && !r.enabled) return false;
			if (STATUSES[statusIdx] === 'Disabled' && r.enabled) return false;
			if (
				needle &&
				![r.ruleName, r.predicate, r.decisionReasonCode ?? '', r.bundle]
					.join(' ')
					.toLowerCase()
					.includes(needle)
			)
				return false;
			return true;
		});
		const by: Record<string, (a: IMonitoringRule, b: IMonitoringRule) => number> = {
			firing: (a, b) => firingOf(b) - firingOf(a) || a.ruleName.localeCompare(b.ruleName),
			score: (a, b) => b.scoreDelta - a.scoreDelta || a.ruleName.localeCompare(b.ruleName),
			name: (a, b) => a.ruleName.localeCompare(b.ruleName),
			bundle: (a, b) =>
				a.bundle.localeCompare(b.bundle) || a.ruleName.localeCompare(b.ruleName),
		};
		return rows.slice().sort(by[sort] ?? by.firing);
	}, [rules, bundle, modeIdx, statusIdx, q, sort, firingOf]);

	/* The bar in the Firing cell is scaled against the largest count IN VIEW, not
	   across the catalogue — the same relative-to-its-own-set scaling the triage
	   deck's leaderboard uses, so filtering to one bundle still shows shape. */
	const maxFiring = useMemo(
		() => Math.max(1, ...shown.map(firingOf)),
		[shown, firingOf]
	);

	const write = useCallback(
		async (rule: IMonitoringRule, body: Record<string, unknown>) => {
			setSaving(rule._id);
			try {
				await tune(rule._id, body);
			} finally {
				setSaving(null);
			}
		},
		[tune]
	);

	/* Committed on blur or Enter, never per keystroke: every keystroke would be a
	   write, and the intermediate "3" on the way to "35" would be saved and
	   scored against. */
	const commitScore = useCallback(
		async (rule: IMonitoringRule) => {
			const raw = scoreDraft[rule._id];
			setScoreDraft(d => {
				const next = { ...d };
				delete next[rule._id];
				return next;
			});
			if (raw === undefined) return;
			const n = Number(raw);
			if (!Number.isFinite(n) || n === rule.scoreDelta) return;
			await write(rule, { scoreDelta: Math.max(0, Math.min(100, Math.round(n))) });
		},
		[scoreDraft, write]
	);

	const active = rules.filter(r => r.enabled && r.mode === 'ACTIVE').length;
	const test = rules.filter(r => r.enabled && r.mode === 'TEST').length;
	const off = rules.filter(r => !r.enabled).length;
	const unshipped = rules.filter(r => !r.predicateImplemented).length;

	const configuring = rules.find(r => r._id === configureId) ?? null;
	const deleting = rules.find(r => r._id === confirmDeleteId) ?? null;

	const tabs = useMemo(
		() => [
			{ key: 'All', label: 'All', count: rules.length },
			...bundles.map(b => {
				const inB = rules.filter(r => r.bundle === b);
				return {
					key: b,
					label: b,
					count: inB.length,
					/* The tab strip itself surfaces a bundle carrying an unshipped
					   predicate, so the gap is visible before anyone opens it. */
					tone: inB.some(r => !r.predicateImplemented) ? ('warn' as const) : undefined,
				};
			}),
		],
		[rules, bundles]
	);

	return (
		<div className="sv2-scope sv2 txn-deck rules-deck">
			<PageHead
				title="Transaction monitoring — rules"
				sub={
					<>
						{dateLine()} · {rules.length} rules · {active} scoring · {test} in test · {off}{' '}
						disabled · ranked by matches in the last {windowAt(winIdx).label}
						{unshipped > 0 && (
							<span className="am">
								{' '}
								· {unshipped} seeded but not yet shipped in the engine
							</span>
						)}
						{failed && (
							<span className="rd"> · catalogue refresh failed, showing the last good list</span>
						)}
						{statsFailed && <span className="am"> · firing counts stale</span>}
					</>
				}
			/>

			<div className="ctl">
				<button
					className="pre"
					type="button"
					onClick={() => setWinIdx(i => (i + 1) % FIRING_WINDOWS.length)}
				>
					Firing window: <b>{windowAt(winIdx).label}</b> ▾
				</button>
				<button
					className={`pre${modeIdx ? ' on' : ''}`}
					type="button"
					onClick={() => setModeIdx(i => (i + 1) % MODES.length)}
				>
					Mode: <b>{MODES[modeIdx]}</b> ▾
				</button>
				<button
					className={`pre${statusIdx ? ' on' : ''}`}
					type="button"
					onClick={() => setStatusIdx(i => (i + 1) % STATUSES.length)}
				>
					Status: <b>{STATUSES[statusIdx]}</b> ▾
				</button>
				<button
					className="pre"
					type="button"
					onClick={() => {
						const keys = Object.keys(SORTS);
						setSort(s => keys[(keys.indexOf(s) + 1) % keys.length] ?? 'firing');
					}}
				>
					Sort: <b>{SORTS[sort]}</b> ▾
				</button>
				<label className="srch" title="Searches rule name, predicate, bundle and decision code">
					<svg viewBox="0 0 24 24">
						<circle cx="11" cy="11" r="7" />
						<line x1="16.5" y1="16.5" x2="21" y2="21" />
					</svg>
					<input placeholder="Search rules" value={q} onChange={e => setQ(e.target.value)} />
				</label>
				<span className="rn">
					{shown.length} of {rules.length} rules
				</span>
			</div>

			<div className="legend">
				chip states:
				<span className="nd review">tuned for this business</span>
				<span className="nd fail">sets a status on match</span>
				<span className="nd test">test mode · no score</span>
				<span className="nd unreached">predicate not shipped · never fires</span>·
				thresholds <span className="mono">review ≥60 · decline ≥85</span>
				{/* THE ONE PLACE THIS WAS EVER SAID. Nothing on this page told the
				    person setting a threshold WHICH figure it is compared against.
				    It is a page-level note and not a per-rule tag on purpose: the
				    engine input carries no fee field at all, so principal-only is
				    true of every rule that reads an amount, not a property of any
				    one predicate — tagging rules individually would invent a
				    metadata field to express something already uniformly true. */}
				{' · '}
				<span className="mono">
					amounts score the principal only — fees are reported, never scored
				</span>
			</div>

			<Tabs tabs={tabs} active={bundle} onChange={setBundle} />

			<div className="panel" style={{ marginTop: 12 }}>
				<div className="ph">
					Rule catalogue
					<span className="r">
						{shown.length} of {rules.length}
					</span>
				</div>
				<div className="tscroll">
					<table className="stbl rules-tbl">
						<thead>
							<tr>
								<th>Rule</th>
								<th className="rt-bundle">Bundle</th>
								<th className="rt-fire">
									Firing · {windowAt(winIdx).label}
									{statsFailed && <span className="am"> stale</span>}
								</th>
								<th className="rt-score">Score</th>
								<th className="rt-mode">Mode</th>
								<th className="rt-on">Enabled</th>
								<th className="rt-act">Actions</th>
							</tr>
						</thead>
						<tbody>
							{!shown.length && (
								<tr>
									<td colSpan={7} className="empty">
										{loading
											? 'Loading the rule catalogue…'
											: failed
												? 'Could not reach transaction monitoring.'
												: rules.length
													? 'No rule matches these filters.'
													: 'The catalogue is empty — see below.'}
									</td>
								</tr>
							)}
							{shown.map(r => {
								const busy = saving === r._id;
								const fired = firingOf(r);
								return (
									<tr key={r._id} className={r.enabled ? '' : 'rt-off'}>
										<td>
											<span className="rt-name">
												{r.ruleName}
												{r.overridden && (
													<span className="nd review" title="Retuned for this business">
														tuned here
													</span>
												)}
												{r.changeStatus && (
													<span
														className="nd fail"
														title={`Sets the transaction to ${r.changeStatus}`}
													>
														sets {r.changeStatus.replace('_', ' ').toLowerCase()}
													</span>
												)}
												{!r.predicateImplemented && (
													<span
														className="nd unreached"
														title="This rule is seeded but the engine has no implementation for its predicate, so it is skipped on every transaction and can never fire."
													>
														predicate not shipped
													</span>
												)}
											</span>
											<span className="rt-sub">
												<span className="mono">{r.predicate}</span> · {r.actions.join(', ')}
												{r.decisionReasonCode && (
													<>
														{' · '}
														<span className="mono">{r.decisionReasonCode}</span>
													</>
												)}
											</span>
										</td>
										<td className="rt-bundle">{r.bundle}</td>
										<td className="rt-fire">
											{/* A stale stats fetch reads as unknown, not as zero — the
											    distinction between "did not fire" and "we do not know"
											    is the whole point of the column. */}
											{statsFailed ? (
												<span className="gy">—</span>
											) : !r.predicateImplemented ? (
												<span className="gy" title="Never runs — see the chip on this rule">
													0 · never
												</span>
											) : (
												<>
													<b className="mono">{fired}</b>
													<span className="rt-bar">
														<i style={{ width: `${Math.round((fired / maxFiring) * 100)}%` }} />
													</span>
												</>
											)}
										</td>
										<td className="rt-score">
											<input
												className="rt-num"
												inputMode="numeric"
												aria-label={`Score added by ${r.ruleName}`}
												disabled={busy || !r.enabled}
												value={scoreDraft[r._id] ?? String(r.scoreDelta)}
												onChange={e =>
													setScoreDraft(d => ({ ...d, [r._id]: e.target.value }))
												}
												onBlur={() => commitScore(r)}
												onKeyDown={e => {
													if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
												}}
											/>
										</td>
										<td className="rt-mode">
											<button
												type="button"
												className={`pre${r.mode === 'TEST' ? ' on' : ''}`}
												disabled={busy || !r.enabled}
												title={
													r.mode === 'TEST'
														? 'Evaluated and recorded, but adds no score'
														: 'Scores every match'
												}
												onClick={() =>
													write(r, { mode: r.mode === 'ACTIVE' ? 'TEST' : 'ACTIVE' })
												}
											>
												{r.mode === 'TEST' ? 'Test' : 'Active'}
											</button>
										</td>
										<td className="rt-on">
											<Switch
												checked={r.enabled}
												disabled={busy}
												label={`${r.enabled ? 'Disable' : 'Enable'} ${r.ruleName}`}
												title={
													r.enabled ? 'Stop evaluating this rule' : 'Evaluate this rule again'
												}
												onChange={next => write(r, { enabled: next })}
											/>
										</td>
										<td className="rt-act">
											<div className="acts">
												<button
													className="dbtn"
													type="button"
													onClick={() => setConfigureId(r._id)}
												>
													Configure
												</button>
												<Kebab
													items={[
														r.overridden
															? {
																	label: 'Delete override (reset to default)',
																	onClick: () => setConfirmDeleteId(r._id),
																}
															: {
																	label: 'Delete',
																	disabled: true,
																	/* `onClick` is required by the primitive; a
																	   disabled item never calls it. */
																	onClick: () => undefined,
																	hint: 'Shared platform default — deleting it would affect every business, or be recreated by the catalogue seed at the next boot. Use the Enabled switch to turn it off for this business only.',
																},
													]}
												/>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>

			{!loading && !rules.length && (
				<Empty>
					The catalogue is empty. It is reconciled on every boot of
					transaction.monitoring.api — if this stays empty, that seed failed and the log line
					is <span className="mono">[monitoring-rules] catalogue seed failed</span>.
				</Empty>
			)}

			<div className="foot">
				A rule is DATA, not code: disabling one or moving it to test takes effect on the next
				transaction with no deploy. Editing a shared default does not change it for anyone else
				— the first edit copies it to this business and that copy is what scores from then on,
				which is what <b>tuned here</b> marks. <b>Test</b> keeps a rule evaluating and recording
				matches on each transaction&apos;s rule runs while contributing nothing to the score,
				which is how a retuned threshold is checked against real traffic before it decides
				anything. The <b>Firing</b> count is matched <i>and</i> scoring runs in the window, from{' '}
				<code>GET /transaction-stats</code> — a rule in test still records runs, so its count is
				what it <i>would</i> have scored on.
			</div>

			<Modal
				open={!!configuring}
				title="Configure rule"
				size="narrow"
				onClose={() => setConfigureId(null)}
				sub={
					configuring
						? `${configuring.ruleName}${saving === configuring._id ? ' · saving…' : ''}`
						: ''
				}
			>
				{configuring && (
					<div className="rules-cfg">
						<div className="grp">Rule</div>
						<table className="kv wide">
							<tbody>
								<tr>
									<td>rule uuid</td>
									<td className="mono">{configuring.ruleUuid}</td>
								</tr>
								<tr>
									<td>predicate</td>
									<td className="mono">{configuring.predicate}</td>
								</tr>
								<tr>
									<td>category</td>
									<td>{configuring.category}</td>
								</tr>
								<tr>
									<td>bundle</td>
									<td>{configuring.bundle}</td>
								</tr>
								<tr>
									<td>actions</td>
									<td>{configuring.actions.join(', ')}</td>
								</tr>
								{configuring.decisionReasonCode && (
									<tr>
										<td>decision code</td>
										<td className="mono">{configuring.decisionReasonCode}</td>
									</tr>
								)}
							</tbody>
						</table>
						{!configuring.predicateImplemented && (
							<div className="cfg-warn">
								<span className="nd unreached">predicate not shipped</span>
								This rule is seeded but the engine has no implementation for{' '}
								<span className="mono">{configuring.predicate}</span>, so it is skipped on
								every transaction and can never fire. Tuning it below changes nothing until
								that engine change ships.
							</div>
						)}

						<div className="grp">Scoring</div>
						<div className="cfg-row">
							<label htmlFor="cfg-score">Score added on match</label>
							<input
								id="cfg-score"
								className="rt-num"
								inputMode="numeric"
								disabled={saving === configuring._id || !configuring.enabled}
								value={scoreDraft[configuring._id] ?? String(configuring.scoreDelta)}
								onChange={e =>
									setScoreDraft(d => ({ ...d, [configuring._id]: e.target.value }))
								}
								onBlur={() => commitScore(configuring)}
								onKeyDown={e => {
									if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
								}}
							/>
						</div>
						<div className="cfg-hint">
							0–100. A rule in <b>test</b> records the match but never adds this.
						</div>

						<div className="cfg-row">
							<span>Mode</span>
							<button
								type="button"
								className={`pre${configuring.mode === 'TEST' ? ' on' : ''}`}
								disabled={saving === configuring._id || !configuring.enabled}
								onClick={() =>
									write(configuring, {
										mode: configuring.mode === 'ACTIVE' ? 'TEST' : 'ACTIVE',
									})
								}
							>
								{configuring.mode === 'TEST' ? 'Test' : 'Active'}
							</button>
						</div>

						<div className="cfg-row">
							<span>Enabled</span>
							<Switch
								checked={configuring.enabled}
								disabled={saving === configuring._id}
								label={`${configuring.enabled ? 'Disable' : 'Enable'} ${configuring.ruleName}`}
								onChange={next => write(configuring, { enabled: next })}
							/>
						</div>

						<div className="cfg-row">
							<label htmlFor="cfg-status">Status set on match</label>
							{/* A constrained select over the model's own enum. The service has
							    accepted `changeStatus` since it existed and no screen ever
							    exposed it, so a rule's hardest-hitting behaviour — deciding the
							    transaction outright — was the one thing not tunable. */}
							<select
								id="cfg-status"
								disabled={saving === configuring._id}
								value={configuring.changeStatus ?? ''}
								onChange={e =>
									write(configuring, { changeStatus: e.target.value || null })
								}
							>
								{CHANGE_STATUSES.map(s => (
									<option key={s || 'none'} value={s}>
										{s ? s.replace('_', ' ').toLowerCase() : 'none — only scores'}
									</option>
								))}
							</select>
						</div>
						<div className="cfg-hint">
							A rule that sets a status decides the transaction on its own, regardless of the
							score. <b>none</b> leaves the decision to the score thresholds.
						</div>

						<div className="cfg-note">
							{configuring.overridden
								? 'This is this business’s own copy of the rule. Deleting the override from the catalogue’s ⋮ menu resets it to simplici’s shared default.'
								: 'Editing this copies it to this business. The shared default keeps scoring every other tenant unchanged.'}
						</div>
					</div>
				)}
			</Modal>

			<Modal
				open={!!deleting}
				title="Delete override"
				size="narrow"
				onClose={() => setConfirmDeleteId(null)}
				sub={deleting ? deleting.ruleName : ''}
				footer={
					deleting && (
						<>
							<button
								className="dbtn"
								type="button"
								onClick={() => setConfirmDeleteId(null)}
							>
								Cancel
							</button>
							<button
								className="dbtn danger"
								type="button"
								style={{ marginLeft: 'auto' }}
								disabled={saving === deleting._id}
								onClick={async () => {
									setSaving(deleting._id);
									try {
										await remove(deleting._id);
										setConfirmDeleteId(null);
										/* The configure panel may be open on the row that has just
										   ceased to exist — its `_id` was the override's, and the
										   reload resolves that name to the shared default under a
										   different id. */
										setConfigureId(null);
									} finally {
										setSaving(null);
									}
								}}
							>
								Delete override
							</button>
						</>
					)
				}
			>
				{deleting && (
					<div className="rules-cfg">
						<p>
							This removes this business&apos;s customization of{' '}
							<b>{deleting.ruleName}</b>. The next transaction is scored against
							simplici&apos;s shared default for that rule — whatever it currently is, which
							may not match the values shown here if the shared default has changed since
							this was overridden.
						</p>
						<table className="kv wide">
							<tbody>
								<tr>
									<td>this copy scores</td>
									<td className="mono">+{deleting.scoreDelta}</td>
								</tr>
								<tr>
									<td>mode</td>
									<td>{deleting.mode.toLowerCase()}</td>
								</tr>
								<tr>
									<td>enabled</td>
									<td>{deleting.enabled ? 'yes' : 'no'}</td>
								</tr>
							</tbody>
						</table>
						<div className="cfg-note">
							The rule itself is not removed — it cannot be, from here. Only this
							business&apos;s copy of it is.
						</div>
					</div>
				)}
			</Modal>
		</div>
	);
};

export default RulesDeck;
